"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, onValue, update, set, remove } from "firebase/database"
import { onAuthStateChanged, getAuth } from "firebase/auth"
import { useNavigate } from "react-router-dom"
import "./userQuotations.css"

import InfoIcon from "./infoIcon"
import {
  Edit,
  Check,
  X,
  MessageSquare,
  Trash2,
  AlertCircle,
  Package,
  Truck,
  Wrench,
  IndianRupee,
  Building2,
  Calendar,
  Filter,
  FileText,
  Handshake,
  CheckCircle,
} from "lucide-react"
import { FaRupeeSign } from "react-icons/fa"

const gifUrl = "/Images/New notification.gif"
const MAX_NEGOTIATION_ROUNDS = 2
const MAX_RESPONSE_LENGTH = 500

// Function to send notifications to users
const sendUserNotification = async (userId, quotationId, title, message) => {
  const db = getDatabase()
  const notificationRef = ref(db, `user_notifications/Quotation Response/${userId}/${quotationId}`)
  const newNotification = {
    title,
    message,
    gifUrl,
    timestamp: new Date().toISOString(),
    seen: false,
  }
  try {
    await set(notificationRef, newNotification)
    console.log("User notification sent successfully")
  } catch (error) {
    console.error("Error sending user notification:", error)
  }
}

// Function to send notifications to suppliers
const sendSupplierNotification = async (userId, quoteId, title, message, siteAddress) => {
  const db = getDatabase()
  const notificationRef = ref(db, `supplier_notifications/Negotiations/${userId}/${quoteId}`)
  const newNotification = {
    title,
    message,
    gifUrl,
    timestamp: new Date().toISOString(),
    seen: false,
    siteAddress: siteAddress,
  }
  try {
    await set(notificationRef, newNotification)
    console.log("Supplier notification sent successfully")
  } catch (error) {
    console.error("Error sending supplier notification:", error)
  }
}

const UserQuotations = ({ adminView = false, selectedBrand = "" }) => {
  const [quotations, setQuotations] = useState([])
  const [submittedQuotations, setSubmittedQuotations] = useState([])
  const [negotiatedQuotations, setNegotiatedQuotations] = useState([])
  const [supplierReplies, setSupplierReplies] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [selectedCompany, setSelectedCompany] = useState("")
  const [companies, setCompanies] = useState([])
  const [companiesWithStats, setCompaniesWithStats] = useState([])
  const [brand, setBrand] = useState(adminView ? selectedBrand : localStorage.getItem("supplierBrand"))
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [activeToggle, setActiveToggle] = useState("new") // new, negotiation, previous
  const [selectedQuotation, setSelectedQuotation] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editedReply, setEditedReply] = useState({})
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [selectedQuotationId, setSelectedQuotationId] = useState(null)
  const authInstance = getAuth()
  const navigate = useNavigate()

  // Update brand when selectedBrand prop changes (for admin view)
  useEffect(() => {
    if (adminView) {
      setBrand(selectedBrand)
    }
  }, [adminView, selectedBrand])

  useEffect(() => {
    const checkAuthState = () => {
      setIsLoading(true)
      setError("")
      if (adminView) {
        fetchQuotations(brand)
        fetchSubmittedQuotations(brand)
        checkForNegotiations(brand)
        checkForNewNegotiations(brand)
      } else {
        const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
          if (user) {
            if (!brand) {
              setError("Brand not selected. Please log in again.")
            } else {
              await fetchQuotations(brand)
              await fetchSubmittedQuotations(brand)
              await checkForNegotiations(brand)
              await checkForNewNegotiations(brand)
            }
          } else {
            setError("User not authenticated. Please log in again.")
          }
          setIsLoading(false)
        })
        return () => unsubscribe()
      }
    }
    checkAuthState()
  }, [brand, adminView, selectedCategory])

  const updateCompaniesWithStats = (newQuotations, submittedQuotations, negotiatedQuotations) => {
    const allQuotations = [...newQuotations, ...submittedQuotations, ...negotiatedQuotations]
    const companyStats = {}

    allQuotations.forEach((quotation) => {
      const companyName = quotation.companyName || "Unknown"
      if (!companyStats[companyName]) {
        companyStats[companyName] = {
          name: companyName,
          newCount: 0,
          negotiationCount: 0,
          previousCount: 0,
          lastQuotationDate: null,
        }
      }

      if (quotation.supplierReply && (quotation.negotiation || quotation.negotiations)) {
        companyStats[companyName].negotiationCount++
      } else if (quotation.supplierReply) {
        companyStats[companyName].previousCount++
      } else {
        companyStats[companyName].newCount++
      }

      const quotationDate = new Date(quotation.timestamp)
      if (
        !companyStats[companyName].lastQuotationDate ||
        quotationDate > new Date(companyStats[companyName].lastQuotationDate)
      ) {
        companyStats[companyName].lastQuotationDate = quotationDate
      }
    })

    const companiesArray = Object.values(companyStats).sort((a, b) => {
      if (a.newCount > 0 && b.newCount === 0) return -1
      if (a.newCount === 0 && b.newCount > 0) return 1
      return new Date(b.lastQuotationDate) - new Date(a.lastQuotationDate)
    })

    setCompaniesWithStats(companiesArray)
    setCompanies(companiesArray.map((c) => c.name))
  }

  // Fetch new quotations that haven't been replied to
  const fetchQuotations = async (selectedBrand) => {
    try {
      const db = getDatabase()
      const quotationsRef = ref(db, "quoteRequests")
      onValue(
        quotationsRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val()
            const filteredQuotations = Object.entries(data).flatMap(([userId, userQuotations]) =>
              Object.entries(userQuotations)
                .filter(([, quote]) => {
                  const categoryMatch =
                    selectedCategory === "all" ||
                    (quote.category && quote.category.toLowerCase() === selectedCategory.toLowerCase())

                  if (adminView && !selectedBrand) {
                    return !quote.supplierReply && categoryMatch && quote.companyName
                  }

                  let brandMatch = false
                  if (quote.category === "cement" || !quote.category) {
                    brandMatch = quote.brand === selectedBrand
                  } else if (quote.category === "steel") {
                    brandMatch = adminView ? true : quote.steelType === selectedBrand
                  } else if (quote.category === "machinery") {
                    brandMatch = adminView ? true : quote.machineryType === selectedBrand
                  }

                  return !quote.supplierReply && brandMatch && categoryMatch && quote.companyName
                })
                .map(([quoteId, quote]) => ({ userId, quoteId, ...quote })),
            )

            // Sort by timestamp - newest first
            filteredQuotations.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            setQuotations(filteredQuotations)
            setError("")
          } else {
            setQuotations([])
            if (!adminView || (adminView && selectedBrand)) {
              setError("No quotations found for this brand.")
            }
          }
          setIsLoading(false)
        },
        (err) => {
          setError("Error fetching quotations: " + err.message)
          setIsLoading(false)
        },
      )
    } catch (err) {
      setError("Error fetching quotations: " + err.message)
      setIsLoading(false)
    }
  }

  // Fetch quotations that have been replied to but not negotiated
  const fetchSubmittedQuotations = async (selectedBrand) => {
    try {
      const db = getDatabase()
      const quotationsRef = ref(db, "quoteRequests")
      onValue(
        quotationsRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val()
            const submitted = Object.entries(data).flatMap(([userId, userQuotations]) =>
              Object.entries(userQuotations)
                .filter(([, quote]) => {
                  const hasSupplierReply = quote.supplierReply && !quote.negotiation && !quote.negotiations
                  const categoryMatch =
                    selectedCategory === "all" ||
                    (quote.category && quote.category.toLowerCase() === selectedCategory.toLowerCase())

                  if (adminView && !selectedBrand) {
                    return hasSupplierReply && categoryMatch && quote.companyName
                  }

                  let brandMatch = false
                  if (quote.category === "Cement" || !quote.category) {
                    brandMatch = quote.brand === selectedBrand
                  } else if (quote.category === "Steel") {
                    brandMatch = adminView ? true : quote.steelType === selectedBrand
                  } else if (quote.category === "Machinery") {
                    brandMatch = adminView ? true : quote.machineryType === selectedBrand
                  }

                  return hasSupplierReply && brandMatch && categoryMatch && quote.companyName
                })
                .map(([quoteId, quote]) => ({ userId, quoteId, ...quote })),
            )

            // Sort by submission date - newest first
            submitted.sort((a, b) => new Date(b.supplierReply.submittedAt) - new Date(a.supplierReply.submittedAt))
            setSubmittedQuotations(submitted)
          } else {
            setSubmittedQuotations([])
          }
        },
        (err) => {
          console.error("Error fetching submitted quotations:", err)
          setSubmittedQuotations([])
        },
      )
    } catch (err) {
      console.error("Error fetching submitted quotations:", err)
      setSubmittedQuotations([])
    }
  }

  // Check for quotations that have been negotiated
  const checkForNegotiations = async (selectedBrand) => {
    try {
      const db = getDatabase()
      const quotationsRef = ref(db, "quoteRequests")
      onValue(
        quotationsRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val()
            const negotiated = Object.entries(data).flatMap(([userId, userQuotations]) =>
              Object.entries(userQuotations)
                .filter(([, quote]) => {
                  const hasNegotiation = quote.supplierReply && (quote.negotiation || quote.negotiations)
                  const categoryMatch =
                    selectedCategory === "all" ||
                    (quote.category && quote.category.toLowerCase() === selectedCategory.toLowerCase())

                  if (adminView && !selectedBrand) {
                    return hasNegotiation && categoryMatch && quote.companyName
                  }

                  let brandMatch = false
                  if (quote.category === "Cement" || !quote.category) {
                    brandMatch = quote.brand === selectedBrand
                  } else if (quote.category === "Steel") {
                    brandMatch = adminView ? true : quote.steelType === selectedBrand
                  } else if (quote.category === "Machinery") {
                    brandMatch = adminView ? true : quote.machineryType === selectedBrand
                  }

                  return hasNegotiation && brandMatch && categoryMatch && quote.companyName
                })
                .map(([quoteId, quote]) => ({ userId, quoteId, ...quote })),
            )

            // Sort by negotiation timestamp - newest first
            negotiated.sort((a, b) => {
              const dateA = new Date(a.negotiation?.timestamp || a.supplierReply.submittedAt)
              const dateB = new Date(b.negotiation?.timestamp || b.supplierReply.submittedAt)
              return dateB - dateA
            })

            setNegotiatedQuotations(negotiated)
          } else {
            setNegotiatedQuotations([])
          }
        },
        (err) => {
          console.error("Error fetching negotiated quotations:", err)
          setNegotiatedQuotations([])
        },
      )
    } catch (err) {
      console.error("Error fetching negotiated quotations:", err)
      setNegotiatedQuotations([])
    }
  }

  // Update companies stats whenever data changes
  useEffect(() => {
    updateCompaniesWithStats(quotations, submittedQuotations, negotiatedQuotations)
  }, [quotations, submittedQuotations, negotiatedQuotations])

  // Check for new negotiations that need notifications
  const checkForNewNegotiations = async (selectedBrand) => {
    try {
      const db = getDatabase()
      const quotationsRef = ref(db, "quoteRequests")
      onValue(
        quotationsRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val()
            Object.entries(data).forEach(([userId, userQuotations]) => {
              Object.entries(userQuotations).forEach(([quoteId, quote]) => {
                let brandMatch = false
                if (quote.category === "Cement" || !quote.category) {
                  brandMatch = quote.brand === selectedBrand
                } else if (quote.category === "Steel") {
                  brandMatch = adminView ? true : quote.steelType === selectedBrand
                } else if (quote.category === "Machinery") {
                  brandMatch = adminView ? true : quote.machineryType === selectedBrand
                }

                if ((adminView && selectedBrand && !brandMatch) || (!adminView && !brandMatch)) {
                  return
                }

                if (quote.supplierReply && quote.negotiation && !quote.negotiation.notified) {
                  sendSupplierNotification(
                    userId,
                    quoteId,
                    "New Negotiation",
                    `${quote.companyName || "A user"} has negotiated your quotation for ${quote.siteAddress}.`,
                    quote.siteAddress,
                  )
                  const negotiationRef = ref(db, `quoteRequests/${userId}/${quoteId}/negotiation/notified`)
                  set(negotiationRef, true)
                }

                if (quote.supplierReply && quote.negotiations) {
                  Object.entries(quote.negotiations).forEach(([negoId, negotiation]) => {
                    if (!negotiation.notified) {
                      sendSupplierNotification(
                        userId,
                        quoteId,
                        "New Negotiation",
                        `${quote.companyName || "A user"} has negotiated your quotation for ${quote.siteAddress}.`,
                        quote.siteAddress,
                      )
                      const negotiationRef = ref(
                        db,
                        `quoteRequests/${userId}/${quoteId}/negotiations/${negoId}/notified`,
                      )
                      set(negotiationRef, true)
                    }
                  })
                }
              })
            })
          }
        },
        (err) => {
          console.error("Error checking for new negotiations:", err)
        },
      )
    } catch (err) {
      console.error("Error checking for new negotiations:", err)
    }
  }

  const handleInputChange = (quoteId, field, value) => {
    const updatedReplies = {
      ...supplierReplies,
      [quoteId]: {
        ...(supplierReplies[quoteId] || {}),
        [field]: value,
      },
    }

    if (field === "pricePerBag") {
      const quote = quotations.find((q) => q.quoteId === quoteId)
      if (quote && quote.quantity) {
        const calculatedTotal = Number.parseFloat(value) * Number.parseInt(quote.quantity)
        updatedReplies[quoteId].totalPrice = calculatedTotal.toFixed(2)
      }
    }

    setSupplierReplies(updatedReplies)
  }

  const handleSendReply = async (quote) => {
    const reply = supplierReplies[quote.quoteId] || {}
    if (!reply.pricePerBag || !reply.totalPrice || !reply.message) {
      setError("Please fill in all fields.")
      return
    }

    try {
      const db = getDatabase()
      const supplierReply = {
        supplierEmail: localStorage.getItem("supplierEmail"),
        pricePerBag: reply.pricePerBag,
        totalPrice: reply.totalPrice,
        message: reply.message,
        submittedAt: new Date().toISOString(),
      }

      const replyRef = ref(db, `quoteRequests/${quote.userId}/${quote.quoteId}/supplierReply`)
      await update(replyRef, supplierReply)

      setSupplierReplies((prev) => ({
        ...prev,
        [quote.quoteId]: {},
      }))

      setSelectedQuotationId(null)
      setSuccessMessage("Quotation successfully submitted!")
      setTimeout(() => setSuccessMessage(""), 6000)

      const notificationMessage = `Your quotation response has been sent for site address: ${quote.siteAddress} and ${quote.grade || quote.category || "item"}`
      await sendUserNotification(quote.userId, quote.quoteId, "Quotation Response", notificationMessage)
    } catch (err) {
      console.error("Error sending reply:", err)
      setError("Failed to send the reply. Please try again.")
    }
  }

  const handleEditQuotation = (quotation) => {
    const hasNegotiations =
      quotation.negotiation || (quotation.negotiations && Object.keys(quotation.negotiations).length > 0)

    if (!hasNegotiations) {
      alert("Only negotiated quotations can be edited.")
      return
    }

    const negotiationRounds = quotation.negotiationHistory ? quotation.negotiationHistory.length : 0
    if (negotiationRounds >= MAX_NEGOTIATION_ROUNDS) {
      alert(`Maximum negotiation rounds (${MAX_NEGOTIATION_ROUNDS}) reached for this quotation.`)
      return
    }

    setSelectedQuotation(quotation)
    setEditedReply({
      pricePerBag: quotation.supplierReply.pricePerBag,
      totalPrice: quotation.supplierReply.totalPrice,
      message: quotation.supplierReply.message,
    })
    setIsEditing(true)
  }

  const handleEditChange = (field, value) => {
    const updatedReply = {
      ...editedReply,
      [field]: value,
    }

    if (field === "pricePerBag" && selectedQuotation) {
      const calculatedTotal = Number.parseFloat(value) * Number.parseInt(selectedQuotation.quantity)
      updatedReply.totalPrice = calculatedTotal.toFixed(2)
    }

    setEditedReply(updatedReply)
  }

  const saveEditedQuotation = async () => {
    if (!selectedQuotation) return

    try {
      const db = getDatabase()
      const previousReply = selectedQuotation.supplierReply
      const negotiationHistory = selectedQuotation.negotiationHistory || []

      let negotiationPrice, negotiationMessage, negotiationTimestamp

      if (selectedQuotation.negotiation) {
        negotiationPrice = selectedQuotation.negotiation.price
        negotiationMessage = selectedQuotation.negotiation.message
        negotiationTimestamp = selectedQuotation.negotiation.timestamp
      } else if (selectedQuotation.negotiations) {
        const negotiationsArray = Object.values(selectedQuotation.negotiations)
        const latestNegotiation = negotiationsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
        negotiationPrice = latestNegotiation.counterOffer
        negotiationMessage = latestNegotiation.message
        negotiationTimestamp = latestNegotiation.timestamp
      }

      negotiationHistory.push({
        ...previousReply,
        negotiationPrice: negotiationPrice,
        negotiationMessage: negotiationMessage,
        timestamp: negotiationTimestamp,
      })

      const updatedReply = {
        ...selectedQuotation.supplierReply,
        pricePerBag: editedReply.pricePerBag,
        totalPrice: editedReply.totalPrice,
        message: editedReply.message,
        updatedAt: new Date().toISOString(),
      }

      const updates = {
        [`quoteRequests/${selectedQuotation.userId}/${selectedQuotation.quoteId}/supplierReply`]: updatedReply,
        [`quoteRequests/${selectedQuotation.userId}/${selectedQuotation.quoteId}/negotiationHistory`]:
          negotiationHistory,
      }

      if (selectedQuotation.negotiation) {
        updates[`quoteRequests/${selectedQuotation.userId}/${selectedQuotation.quoteId}/negotiation/status`] =
          "counter-offered"
      } else if (selectedQuotation.negotiations) {
        const negotiationsKeys = Object.keys(selectedQuotation.negotiations)
        const latestKey = negotiationsKeys.sort((a, b) => {
          const timeA = selectedQuotation.negotiations[a].timestamp
          const timeB = selectedQuotation.negotiations[b].timestamp
          return new Date(timeB) - new Date(timeA)
        })[0]

        updates[
          `quoteRequests/${selectedQuotation.userId}/${selectedQuotation.quoteId}/negotiations/${latestKey}/status`
        ] = "counter-offered"
      }

      await update(ref(db), updates)

      const notificationMessage = `Your negotiated quotation for ${selectedQuotation.siteAddress} has been updated with a new price of ${editedReply.pricePerBag} per bag (total: ${editedReply.totalPrice})`
      await sendUserNotification(
        selectedQuotation.userId,
        selectedQuotation.quoteId,
        "Negotiation Response",
        notificationMessage,
      )

      setIsEditing(false)
      setSelectedQuotation(null)
      setSuccessMessage("Negotiation response successfully sent!")
      setTimeout(() => setSuccessMessage(""), 6000)
    } catch (error) {
      console.error("Error updating quotation:", error)
      setError("Failed to update the quotation. Please try again.")
    }
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setSelectedQuotation(null)
  }

  const handleDeleteQuotation = async (quotation) => {
    if (!quotation.supplierReply) {
      return
    }

    if (deleteConfirm === quotation.quoteId) {
      try {
        const db = getDatabase()
        await remove(ref(db, `quoteRequests/${quotation.userId}/${quotation.quoteId}`))
        setSuccessMessage("Quotation deleted successfully")
        setTimeout(() => setSuccessMessage(""), 6000)
        setDeleteConfirm(null)
      } catch (error) {
        console.error("Error deleting quotation:", error)
        setError("Failed to delete the quotation. Please try again.")
      }
    } else {
      setDeleteConfirm(quotation.quoteId)
      setTimeout(() => setDeleteConfirm(null), 3000)
    }
  }

  const hasNegotiations = (quote) => {
    return quote.negotiation || (quote.negotiations && Object.keys(quote.negotiations).length > 0)
  }

  const getNegotiationStatus = (quote) => {
    if (quote.negotiation) {
      return quote.negotiation.status || "pending"
    } else if (quote.negotiations) {
      const negotiationsArray = Object.values(quote.negotiations)
      if (negotiationsArray.length > 0) {
        const latestNegotiation = negotiationsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
        return latestNegotiation.status || "pending"
      }
    }
    return "pending"
  }

  const getNegotiationPrice = (quote) => {
    if (quote.negotiation) {
      return quote.negotiation.price
    } else if (quote.negotiations) {
      const negotiationsArray = Object.values(quote.negotiations)
      if (negotiationsArray.length > 0) {
        const latestNegotiation = negotiationsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
        return latestNegotiation.counterOffer
      }
    }
    return ""
  }

  const getNegotiationMessage = (quote) => {
    if (quote.negotiation) {
      return quote.negotiation.message
    } else if (quote.negotiations) {
      const negotiationsArray = Object.values(quote.negotiations)
      if (negotiationsArray.length > 0) {
        const latestNegotiation = negotiationsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
        return latestNegotiation.message
      }
    }
    return ""
  }

  const getNegotiationTimestamp = (quote) => {
    if (quote.negotiation) {
      return quote.negotiation.timestamp
    } else if (quote.negotiations) {
      const negotiationsArray = Object.values(quote.negotiations)
      if (negotiationsArray.length > 0) {
        const latestNegotiation = negotiationsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
        return latestNegotiation.timestamp
      }
    }
    return quote.supplierReply.submittedAt
  }

  const getCategoryIcon = (category) => {
    if (!category || category === "Cement") {
      return <Package size={16} />
    } else if (category === "Steel") {
      return <Package size={16} />
    } else if (category === "Machinery") {
      return <Wrench size={16} />
    }
    return <Package size={16} />
  }

  // Filter quotations based on selected company
  const filteredNewQuotations = selectedCompany
    ? quotations.filter((q) => q.companyName === selectedCompany)
    : quotations

  const filteredNegotiatedQuotations = selectedCompany
    ? negotiatedQuotations.filter((q) => q.companyName === selectedCompany)
    : negotiatedQuotations

  const filteredSubmittedQuotations = selectedCompany
    ? submittedQuotations.filter((q) => q.companyName === selectedCompany)
    : submittedQuotations

  if (isLoading)
    return (
      <div className="uq-loading-container">
        <div className="uq-spinner"></div>
        <p>Loading quotations...</p>
      </div>
    )

  return (
    <div className="uq-quotations-container">
      {/* Header */}
      <div className="uq-quotations-header">
        <h1>
          User Quotations {adminView && selectedBrand ? `- ${selectedBrand}` : adminView ? "- All Brands" : ""}
          <InfoIcon text="This section allows you to manage user quotations. You can respond to new quotations, view your submitted responses, and handle negotiations. You can edit negotiated quotations up to 3 times to provide counter-offers." />
        </h1>
        {/* Category Filter - Only for admin */}
        {adminView && (
          <div className="uq-category-filter">
            <Filter size={16} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="uq-category-select"
            >
              <option value="all">All Categories</option>
              <option value="cement">Cement</option>
              <option value="steel">Steel</option>
              <option value="machinery">Machinery</option>
            </select>
          </div>
        )}
      </div>

      {error && <div className="uq-error-message">{error}</div>}
      {successMessage && <div className="uq-success-message">{successMessage}</div>}

      {/* Two Column Layout */}
      <div className="uq-two-column-layout">
        {/* Left Column - Company Filter */}
        <div className="uq-left-column">
          <div className="uq-column-header">
            <Building2 size={18} />
            <h3>Companies</h3>
          </div>
          <div className="uq-company-filter-list">
            <div
              className={`uq-company-filter-item ${selectedCompany === "" ? "uq-active" : ""}`}
              onClick={() => setSelectedCompany("")}
            >
              <div className="uq-company-info">
                <span className="uq-company-name">All Companies</span>
                <div className="uq-company-stats">
                  <span className="uq-new-count">{quotations.length} new</span>
                  <span className="uq-negotiation-count">{negotiatedQuotations.length} negotiations</span>
                  <span className="uq-previous-count">{submittedQuotations.length} previous</span>
                </div>
              </div>
            </div>
            {companiesWithStats.map((company, index) => (
              <div
                key={index}
                className={`uq-company-filter-item ${selectedCompany === company.name ? "uq-active" : ""}`}
                onClick={() => setSelectedCompany(company.name)}
              >
                <div className="uq-company-info">
                  <span className="uq-company-name">{company.name}</span>
                  <div className="uq-company-stats">
                    {company.newCount > 0 && <span className="uq-new-count">{company.newCount} new</span>}
                    {company.negotiationCount > 0 && (
                      <span className="uq-negotiation-count">{company.negotiationCount} negotiations</span>
                    )}
                    {company.previousCount > 0 && (
                      <span className="uq-previous-count">{company.previousCount} previous</span>
                    )}
                  </div>
                  {company.lastQuotationDate && (
                    <div className="uq-last-quotation-date">
                      <Calendar size={12} />
                      <span>{company.lastQuotationDate.toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                {company.newCount > 0 && <div className="uq-new-indicator"></div>}
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Content Display with Toggle Buttons */}
        <div className="uq-right-column">
          <div className="uq-column-header">
            <MessageSquare size={18} />
            <h3>Quotation Management</h3>
          </div>

          {/* Toggle Buttons */}
          <div className="uq-toggle-buttons">
            <button
              className={`uq-toggle-button ${activeToggle === "new" ? "uq-active" : ""}`}
              onClick={() => setActiveToggle("new")}
            >
              <FileText size={16} />
              <span>New Quotations</span>
              <span className="uq-toggle-count">{filteredNewQuotations.length}</span>
            </button>
            <button
              className={`uq-toggle-button ${activeToggle === "negotiation" ? "uq-active" : ""}`}
              onClick={() => setActiveToggle("negotiation")}
            >
              <Handshake size={16} />
              <span>Negotiation Responses</span>
              <span className="uq-toggle-count">{filteredNegotiatedQuotations.length}</span>
            </button>
            <button
              className={`uq-toggle-button ${activeToggle === "previous" ? "uq-active" : ""}`}
              onClick={() => setActiveToggle("previous")}
            >
              <CheckCircle size={16} />
              <span>Previous Quotations</span>
              <span className="uq-toggle-count">{filteredSubmittedQuotations.length}</span>
            </button>
          </div>

          {/* Content Area */}
          <div className="uq-content-area">
            {/* New Quotations Content */}
            {activeToggle === "new" && (
              <div className="uq-quotations-list">
                {filteredNewQuotations.length > 0 ? (
                  filteredNewQuotations.map((quote) => {
                    const category = quote.category?.toLowerCase()
                    const isSelected = selectedQuotationId === quote.quoteId
                    return (
                      <div
                        key={quote.quoteId}
                        className={`uq-quotation-card uq-new ${isSelected ? "uq-selected" : ""}`}
                        onClick={() => setSelectedQuotationId(isSelected ? null : quote.quoteId)}
                      >
                        <div className="uq-card-header">
                          <div className="uq-company-info">
                            <h4>{quote.companyName}</h4>
                            <span className="uq-site-address">{quote.siteAddress}</span>
                          </div>
                          <div className="uq-category-badge">
                            {getCategoryIcon(category)}
                            <span>{quote.category || "Cement"}</span>
                          </div>
                        </div>
                        <div className="uq-quotation-preview">
                          <div className="uq-detail-row">
                            <span>Quantity:</span>
                            <span>
                              {quote.quantity} {quote.quantityUnit || "units"}
                            </span>
                          </div>
                          <div className="uq-quotation-timestamp">{new Date(quote.timestamp).toLocaleDateString()}</div>
                        </div>
                        {isSelected && (
                          <div className="uq-quotation-details-expanded">
                            <div className="uq-quotation-details">
                              <div className="uq-detail-row">
                                <span>User Name:</span>
                                <span>{quote.contactInfo?.name || "N/A"}</span>
                              </div>
                              <div className="uq-detail-row">
                                <span>Phone:</span>
                                <span>{quote.contactInfo?.phone || "N/A"}</span>
                              </div>
                              {(category === "cement") && (
                                <>
                                  <div className="uq-detail-row">
                                    <span>Brand:</span>
                                    <span>{quote.brand}</span>
                                  </div>
                                  <div className="uq-detail-row">
                                    <span>Grade:</span>
                                    <span>{quote.grade}</span>
                                  </div>
                                </>
                              )}
                              {category === "steel" && (
                                <>
                                  <div className="uq-detail-row">
                                    <span>Steel Type:</span>
                                    <span>{quote.steelType}</span>
                                  </div>
                                  <div className="uq-detail-row">
                                    <span>Steel Grade:</span>
                                    <span>{quote.steelGrade || "N/A"}</span>
                                  </div>
                                </>
                              )}
                              {category === "machinery" && (
                                <>
                                  <div className="uq-detail-row">
                                    <span>Machinery Type:</span>
                                    <span>{quote.machineryType}</span>
                                  </div>
                                  <div className="uq-detail-row">
                                    <span>Capacity:</span>
                                    <span>{quote.machineryCapacity || "N/A"}</span>
                                  </div>
                                  <div className="uq-detail-row">
                                    <span>Condition:</span>
                                    <span>{quote.machineryCondition || "N/A"}</span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="uq-reply-form">
                              <h5>Your Quotation</h5>
                              <div className="uq-form-group">
                                <label>
                                  <FaRupeeSign size={16} /> Price Per {category === "machinery" ? "Unit" : "Bag"} *
                                </label>
                                <input
                                  type="number"
                                  placeholder={`Enter price per ${category === "machinery" ? "unit" : "bag"}`}
                                  value={supplierReplies[quote.quoteId]?.pricePerBag || ""}
                                  onChange={(e) => handleInputChange(quote.quoteId, "pricePerBag", e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="uq-form-group">
                                <label>
                                  <FaRupeeSign size={16} /> Total Price *
                                </label>
                                <input
                                  type="number"
                                  placeholder="Enter total price"
                                  value={supplierReplies[quote.quoteId]?.totalPrice || ""}
                                  onChange={(e) => handleInputChange(quote.quoteId, "totalPrice", e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="uq-form-group">
                                <label>
                                  <MessageSquare size={16} /> Message *
                                </label>
                                <textarea
                                  placeholder="Enter your message"
                                  maxLength={MAX_RESPONSE_LENGTH}
                                  value={supplierReplies[quote.quoteId]?.message || ""}
                                  onChange={(e) => handleInputChange(quote.quoteId, "message", e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  rows={3}
                                />
                              </div>
                              <button
                                className="uq-submit-button"
                                onClick={() => handleSendReply(quote)}
                                disabled={
                                  !supplierReplies[quote.quoteId]?.pricePerBag ||
                                  !supplierReplies[quote.quoteId]?.totalPrice ||
                                  !supplierReplies[quote.quoteId]?.message
                                }
                              >
                                Send Quotation
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
                ) : (
                  <div className="uq-no-quotations">
                    <FileText size={48} />
                    <p>No new quotations found</p>
                  </div>
                )}
              </div>
            )}

            {/* Negotiation Responses Content */}
            {activeToggle === "negotiation" && (
              <div className="uq-quotations-list">
                {filteredNegotiatedQuotations.length > 0 ? (
                  filteredNegotiatedQuotations.map((quote) => (
                    <div key={quote.quoteId} className="uq-quotation-card uq-negotiated">
                      <div className="uq-card-header">
                        <div className="uq-company-info">
                          <h4>{quote.companyName}</h4>
                          <span className="uq-site-address">{quote.siteAddress}</span>
                        </div>
                        <div className="uq-card-actions">
                          <div className="uq-category-badge">
                            {getCategoryIcon(quote.category)}
                            <span>{quote.category || "Cement"}</span>
                          </div>
                          <button
                            className="uq-edit-button"
                            onClick={() => handleEditQuotation(quote)}
                            disabled={
                              !hasNegotiations(quote) ||
                              (quote.negotiationHistory && quote.negotiationHistory.length >= MAX_NEGOTIATION_ROUNDS)
                            }
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className={`uq-delete-button ${deleteConfirm === quote.quoteId ? "uq-confirm" : ""}`}
                            onClick={() => handleDeleteQuotation(quote)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="uq-quotation-details">
                        <div className="uq-detail-row">
                          <span>Quantity:</span>
                          <span>
                            {quote.quantity} {quote.quantityUnit || "units"}
                          </span>
                        </div>
                        <div className="uq-negotiation-info">
                          <div className="uq-negotiation-header">
                            <h5>Negotiation Details</h5>
                            <span className={`uq-status-badge ${getNegotiationStatus(quote)}`}>
                              {getNegotiationStatus(quote)}
                            </span>
                          </div>
                          <div className="uq-price-comparison">
                            <div className="uq-price-item">
                              <span>Your Price:</span>
                              <span className="uq-price">
                                {quote.supplierReply.pricePerBag} per {quote.category === "Machinery" ? "unit" : "bag"}
                              </span>
                              <span className="uq-total">Total: {quote.supplierReply.totalPrice}</span>
                            </div>
                            <div className="uq-price-item uq-negotiated">
                              <span>Customer's Offer:</span>
                              <span className="uq-price">
                                {getNegotiationPrice(quote)} per {quote.category === "Machinery" ? "unit" : "bag"}
                              </span>
                              <span className="uq-message">"{getNegotiationMessage(quote)}"</span>
                            </div>
                          </div>
                          {quote.negotiationHistory && quote.negotiationHistory.length > 0 && (
                            <div className="uq-negotiation-rounds">
                              <span className="uq-rounds-info">
                                Negotiation rounds: {quote.negotiationHistory.length}/{MAX_NEGOTIATION_ROUNDS}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="uq-quotation-timestamp">
                          Negotiated: {new Date(getNegotiationTimestamp(quote)).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="uq-no-quotations">
                    <Handshake size={48} />
                    <p>No negotiation responses found</p>
                  </div>
                )}
              </div>
            )}

            {/* Previous Quotations Content */}
            {activeToggle === "previous" && (
              <div className="uq-quotations-list">
                {filteredSubmittedQuotations.length > 0 ? (
                  filteredSubmittedQuotations.map((quote) => (
                    <div key={quote.quoteId} className="uq-quotation-card uq-previous">
                      <div className="uq-card-header">
                        <div className="uq-company-info">
                          <h4>{quote.companyName}</h4>
                          <span className="uq-site-address">{quote.siteAddress}</span>
                        </div>
                        <div className="uq-card-actions">
                          <div className="uq-category-badge">
                            {getCategoryIcon(quote.category)}
                            <span>{quote.category || "Cement"}</span>
                          </div>
                          <button
                            className={`uq-delete-button ${deleteConfirm === quote.quoteId ? "uq-confirm" : ""}`}
                            onClick={() => handleDeleteQuotation(quote)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="uq-quotation-details">
                        <div className="uq-detail-row">
                          <span>Quantity:</span>
                          <span>
                            {quote.quantity} {quote.quantityUnit || "units"}
                          </span>
                        </div>
                        <div className="uq-detail-row">
                          <span>Price Per {quote.category === "Machinery" ? "Unit" : "Bag"}:</span>
                          <span>{quote.supplierReply.pricePerBag}</span>
                        </div>
                        <div className="uq-detail-row">
                          <span>Total Price:</span>
                          <span>{quote.supplierReply.totalPrice}</span>
                        </div>
                        <div className="uq-response-preview">
                          <h5>Your Message:</h5>
                          <p>{quote.supplierReply.message?.substring(0, 100)}...</p>
                        </div>
                        <div className="uq-quotation-timestamp">
                          Submitted: {new Date(quote.supplierReply.submittedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="uq-no-quotations">
                    <CheckCircle size={48} />
                    <p>No previous quotations found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditing && selectedQuotation && (
        <div className="uq-modal-overlay">
          <div className="uq-edit-modal">
            <div className="uq-modal-header">
              <h3>Respond to Negotiation</h3>
              <button className="uq-close-button" onClick={cancelEdit}>
                ×
              </button>
            </div>
            <div className="uq-modal-content">
              <div className="uq-quotation-info">
                <div className="uq-info-row">
                  <span>Company:</span>
                  <span>{selectedQuotation.companyName}</span>
                </div>
                <div className="uq-info-row">
                  <span>Site Address:</span>
                  <span>{selectedQuotation.siteAddress}</span>
                </div>
                <div className="uq-info-row">
                  <span>Category:</span>
                  <span>{selectedQuotation.category || "Cement"}</span>
                </div>
                <div className="uq-info-row">
                  <span>Quantity:</span>
                  <span>
                    {selectedQuotation.quantity} {selectedQuotation.quantityUnit || "units"}
                  </span>
                </div>
              </div>
              <div className="uq-negotiation-details">
                <h4>Negotiation Details</h4>
                <div className="uq-negotiation-comparison">
                  <div className="uq-price-column">
                    <div className="uq-price-header">Your Original Price</div>
                    <div className="uq-price-value">
                      {selectedQuotation.supplierReply.pricePerBag} per{" "}
                      {selectedQuotation.category === "Machinery" ? "unit" : "bag"}
                    </div>
                    <div className="uq-price-total">Total: {selectedQuotation.supplierReply.totalPrice}</div>
                  </div>
                  <div className="uq-price-column uq-negotiated">
                    <div className="uq-price-header">Customer's Offer</div>
                    <div className="uq-price-value">
                      {getNegotiationPrice(selectedQuotation)} per{" "}
                      {selectedQuotation.category === "Machinery" ? "unit" : "bag"}
                    </div>
                    <div className="uq-price-message">"{getNegotiationMessage(selectedQuotation)}"</div>
                  </div>
                </div>
              </div>
              <div className="uq-negotiation-response">
                <h4>Your Counter-Offer</h4>
                <div className="uq-form-group">
                  <label>
                    <IndianRupee size={16} /> Price Per {selectedQuotation.category === "Machinery" ? "Unit" : "Bag"} *
                  </label>
                  <input
                    value={editedReply.pricePerBag}
                    onChange={(e) => handleEditChange("pricePerBag", e.target.value)}
                    className="uq-modal-input"
                  />
                </div>
                <div className="uq-form-group">
                  <label>
                    <MessageSquare size={16} /> Message *
                  </label>
                  <textarea
                    value={editedReply.message}
                    onChange={(e) => handleEditChange("message", e.target.value)}
                    className="uq-modal-input"
                    placeholder="Explain your counter-offer"
                    rows={4}
                  />
                </div>
                <div className="uq-negotiation-limits">
                  <AlertCircle size={14} />
                  <span>
                    Negotiation round {(selectedQuotation.negotiationHistory?.length || 0) + 1}/{MAX_NEGOTIATION_ROUNDS}
                  </span>
                </div>
              </div>
              <div className="uq-modal-actions">
                <button onClick={saveEditedQuotation} className="uq-save-button">
                  <Check size={16} /> Send Counter-Offer
                </button>
                <button onClick={cancelEdit} className="uq-cancel-button">
                  <X size={16} /> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserQuotations